<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useMapGetter } from 'dashboard/composables/store';
import SalesDetailView from '../components/SalesDetailView.vue';
import { useSalesTransactionDetail } from '../composables/useSalesTransactionDetail';

const NUCLEO_TAB_ORDER = Object.freeze([
  'nucleo',
  'property',
  'sellers',
  'buyers',
  'history',
]);
const SALES_TAB_ORDER = Object.freeze([
  'sale',
  'property',
  'sellers',
  'buyers',
  'history',
]);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const transactionId = computed(() =>
  route.params.salesTransactionId?.toString()
);
const detail = useSalesTransactionDetail({ id: transactionId, t });
const nucleoContext = computed(
  () => route.name === 'operacao_imobiliaria_nucleo_venda_detalhes'
);
const initialTab = computed(() =>
  nucleoContext.value ? 'nucleo' : route.query.detail_tab || 'sale'
);
const tabOrder = computed(() =>
  nucleoContext.value ? NUCLEO_TAB_ORDER : SALES_TAB_ORDER
);

const dashboardQuery = computed(() => {
  const {
    detail_tab: _detailTab,
    imovel_listing_id: _listingId,
    work_order_id: _workOrderId,
    ...query
  } = route.query;
  return query;
});

const backToSales = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas',
    params: { accountId: route.params.accountId },
    query: dashboardQuery.value,
  });
const backToNucleo = () => {
  const listingId = route.query.imovel_listing_id;
  const workOrderId = route.query.work_order_id;
  if (listingId && workOrderId) {
    router.push({
      name: 'operacao_imobiliaria_nucleo_detalhe',
      params: {
        accountId: route.params.accountId,
        imovelListingId: listingId,
        workOrderId,
      },
    });
    return;
  }
  router.push({
    name: 'operacao_imobiliaria_nucleo',
    params: { accountId: route.params.accountId },
  });
};
const goBack = () => (nucleoContext.value ? backToNucleo() : backToSales());
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <div
      v-if="detail.loading.value"
      class="flex min-h-64 items-center justify-center p-8 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.COMMON.LOADING') }}
    </div>
    <section
      v-else-if="detail.error.value"
      class="mx-auto flex min-h-64 w-full max-w-[1500px] flex-col items-center justify-center px-4 py-8 text-center sm:px-6 lg:px-10"
    >
      <p class="text-sm text-lais-danger">{{ detail.error.value }}</p>
      <LaisPrimaryButton class="mt-4" variant="ghost" @click="goBack">
        {{ t('SALES.ACTIONS.BACK') }}
      </LaisPrimaryButton>
    </section>
    <SalesDetailView
      v-else-if="detail.transaction.value"
      :initial-tab="initialTab"
      :show-nucleo-link="!nucleoContext"
      :tab-order="tabOrder"
      :transaction="detail.transaction.value"
      @back="goBack"
    />
  </LaisGamificationSurface>
</template>
